import React from "react";
import api from "@/lib/api";

const WA_MESSAGE = "Ciao, ti ho visto su HostMarket e vorrei sapere qualche informazione in più.";

// Fire-and-forget click tracking (doesn't block navigation)
const trackClick = (listingId, kind) => {
  if (!listingId) return;
  try { api.post(`/listings/${listingId}/track-click`, { kind }); } catch { /* noop */ }
};

/**
 * Fancy contact buttons: Email (magenta), Phone (gold), WhatsApp (green with pre-filled msg).
 * Hover glow, icons, big touch targets. Tracks clicks on each button.
 */
export default function ContactButtons({ listing }) {
  const hasAny = listing.contact_email || listing.contact_phone || listing.whatsapp || listing.website;
  if (!hasAny) return <div className="text-sm text-zinc-500" data-testid="contact-empty">Nessun contatto fornito.</div>;

  const waNumber = (listing.whatsapp || "").replace(/[^0-9]/g, "");
  const waUrl = waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(WA_MESSAGE)}` : null;

  return (
    <div className="flex flex-col gap-3" data-testid="contact-buttons">
      {listing.contact_email && (
        <a
          onClick={() => trackClick(listing.id, "email")}
          href={`mailto:${listing.contact_email}?subject=${encodeURIComponent("Richiesta informazioni da HostMarket")}&body=${encodeURIComponent(WA_MESSAGE)}`}
          className="group relative overflow-hidden rounded-lg px-5 py-4 bg-gradient-to-r from-[#E619A8] to-[#ff2fb4] text-white font-bold uppercase tracking-wider text-sm transition-all hover:shadow-[0_0_40px_-5px_rgba(230,25,168,0.6)] hover:-translate-y-0.5"
          data-testid="btn-email"
        >
          <span className="absolute inset-0 bg-white/0 group-hover:bg-white/5 transition-colors"></span>
          <span className="relative flex items-center gap-3">
            <span className="w-9 h-9 rounded-md bg-white/15 flex items-center justify-center"><i className="fas fa-envelope text-lg"></i></span>
            <span className="flex-1 flex flex-col items-start">
              <span className="text-[10px] opacity-70">Scrivi un'email</span>
              <span className="text-sm truncate max-w-[200px]">{listing.contact_email}</span>
            </span>
            <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
          </span>
        </a>
      )}

      {listing.contact_phone && (
        <a
          onClick={() => trackClick(listing.id, "phone")}
          href={`tel:${listing.contact_phone.replace(/\s+/g, "")}`}
          className="group relative overflow-hidden rounded-lg px-5 py-4 bg-gradient-to-r from-[#FFBE00] to-[#FFD84D] text-black font-bold uppercase tracking-wider text-sm transition-all hover:shadow-[0_0_40px_-5px_rgba(255,190,0,0.55)] hover:-translate-y-0.5"
          data-testid="btn-phone"
        >
          <span className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors"></span>
          <span className="relative flex items-center gap-3">
            <span className="w-9 h-9 rounded-md bg-black/10 flex items-center justify-center"><i className="fas fa-phone text-lg"></i></span>
            <span className="flex-1 flex flex-col items-start">
              <span className="text-[10px] opacity-70">Chiama ora</span>
              <span className="text-sm">{listing.contact_phone}</span>
            </span>
            <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
          </span>
        </a>
      )}

      {waUrl && (
        <a
          onClick={() => trackClick(listing.id, "whatsapp")}
          href={waUrl}
          target="_blank"
          rel="noreferrer"
          className="group relative overflow-hidden rounded-lg px-5 py-4 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-bold uppercase tracking-wider text-sm transition-all hover:shadow-[0_0_40px_-5px_rgba(37,211,102,0.55)] hover:-translate-y-0.5"
          data-testid="btn-whatsapp"
        >
          <span className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors"></span>
          <span className="relative flex items-center gap-3">
            <span className="w-9 h-9 rounded-md bg-white/15 flex items-center justify-center"><i className="fab fa-whatsapp text-xl"></i></span>
            <span className="flex-1 flex flex-col items-start">
              <span className="text-[10px] opacity-80">Scrivi su WhatsApp · testo già pronto</span>
              <span className="text-sm">+{waNumber}</span>
            </span>
            <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
          </span>
        </a>
      )}

      {listing.website && (
        <a
          onClick={() => trackClick(listing.id, "website")}
          href={listing.website.startsWith("http") ? listing.website : `https://${listing.website}`}
          target="_blank"
          rel="noreferrer"
          className="group flex items-center gap-3 rounded-lg px-5 py-3 border border-zinc-700 text-zinc-200 hover:border-[#FFBE00] hover:text-[#FFBE00] transition-all"
          data-testid="btn-website"
        >
          <i className="fas fa-globe"></i>
          <span className="flex-1 text-sm truncate">{listing.website.replace(/^https?:\/\//, "")}</span>
          <i className="fas fa-arrow-up-right-from-square text-[10px]"></i>
        </a>
      )}
    </div>
  );
}
